import React, { useState, useRef, useEffect } from 'react';
import { Filter, Search, RefreshCw, User, UserCheck, ChevronDown, X, GitBranch, Tag } from 'lucide-react';
import CalendarFilter from '../../common/CalendarFilter';

const TimeTrackingFilters = ({ 
  filters, 
  onFilterChange, 
  onApplyFilters, 
  onResetFilters, 
  onRefreshData,
  loading, 
  filtersChanged,
  filterOptions,
}) => {
    const [dropdownStates, setDropdownStates] = useState({
    worklogAuthor: { isOpen: false, search: '' },
    issueAssignee: { isOpen: false, search: '' },
    project: { isOpen: false, search: '' },
    status: { isOpen: false, search: '' },
    priority: { isOpen: false, search: '' },
    sprint: { isOpen: false, search: '' }
  });

  const dropdownRefs = useRef({});
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(dropdownRefs.current).forEach(key => {
        if (dropdownRefs.current[key] && !dropdownRefs.current[key].contains(event.target)) {
          setDropdownStates(prev => ({
            ...prev,
            [key]: { ...prev[key], isOpen: false }
          }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle dropdown
  const toggleDropdown = (field) => {
    setDropdownStates(prev => ({
      ...prev,
      [field]: { 
        ...prev[field], 
        isOpen: !prev[field].isOpen,
        search: ''
      }
    }));
  };

  // Update search in dropdown
  const updateSearch = (field, value) => {
    setDropdownStates(prev => ({
      ...prev,
      [field]: { ...prev[field], search: value }
    }));
  };

  // Handle multi-select toggle
  const handleMultiToggle = (field, value) => {
    const currentValues = filters[field] || [];

    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    onFilterChange(field, newValues);
  };

  // Remove single filter value
  const removeFilterValue = (field, value) => {
    const currentValues = filters[field] || [];
    const newValues = currentValues.filter(v => v !== value);
    onFilterChange(field, newValues);
  };

  // Get filtered options based on search
  const getFilteredOptions = (field, options) => {
    if (!options || !Array.isArray(options)) return [];
    
    const search = dropdownStates[field].search.toLowerCase();
    if (!search) return options;
    
    return options.filter(option => {
      const text = typeof option === 'string' ? option : 
                   option.display_name || option.name || option.key || option.label || '';
      return text.toLowerCase().includes(search);
    });
  };

  // Get display name for option
  const getDisplayName = (option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.display_name || option.name || option.key || option.label || '';
  };

  // Get unique key for option
  const getOptionKey = (option, index) => {
    if (typeof option === 'string') return `${option}_${index}`;
    return option.account_id || option.id || option.key || option.value || `option_${index}`;
  };

  // Render multi-select dropdown
  const renderMultiSelectDropdown = (field, options, placeholder, icon) => {
    const isOpen = dropdownStates[field].isOpen;
    const search = dropdownStates[field].search;
    const filteredOptions = getFilteredOptions(field, options);
    const selectedValues = filters[field] || [];

    // sprint options are project-specific so they require a project selection.
    // worklogAuthor and issueAssignee can be queried without a project.
    const requiresProjectSelection = field === 'sprint';
    const hasProjectSelected = Array.isArray(filters.project) && filters.project.length > 0;

    return (
      <div 
        ref={el => dropdownRefs.current[field] = el}
        className="relative"
      >
        <button
          type="button"
          onClick={() => toggleDropdown(field)}
          className={`w-full px-3 py-2 text-left border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between ${
            selectedValues.length > 0 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
              : 'border-gray-300 dark:border-white/20 bg-white dark:bg-slate-800'
          } text-gray-900 dark:text-slate-100`}
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {icon}
            <span className="truncate">
              {selectedValues.length === 0 
                ? placeholder 
                : `${selectedValues.length} selected`
              }
            </span>
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Selected values tags */}
        {selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {selectedValues.slice(0, 3).map((value, index) => {
              const option = options?.find(opt => 
                (typeof opt === 'string' ? opt : (opt.account_id || opt.id || opt.key)) === value
              );
              return (
                <span
                  key={`${field}_selected_${index}`}
                  className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                >
                  {getDisplayName(option) || value}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFilterValue(field, value);
                    }}
                    className="ml-1 hover:text-blue-600 dark:hover:text-blue-200"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
            {selectedValues.length > 3 && (
              <span className="px-2 py-1 text-xs text-gray-500 dark:text-slate-400">
                +{selectedValues.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-white/20 rounded-md shadow-lg max-h-60 overflow-hidden left-0 right-0">
            {/* Search input */}
            <div className="p-2 border-b border-gray-200 dark:border-white/10">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-400" />
                <input
                  type="text"
                  placeholder={`Search ${field}...`}
                  value={search}
                  onChange={(e) => updateSearch(field, e.target.value)}
                  className="w-full pl-8 pr-2 py-1 text-sm border border-gray-300 dark:border-white/20 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-500"
                  autoFocus
                />
              </div>
            </div>
            
            {/* Options */}
            <div className="max-h-40 overflow-y-auto">
              {requiresProjectSelection && !hasProjectSelected ? (
                <div className="px-3 py-2 text-sm text-red-500 dark:text-red-400">
                  Please select a project first
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-slate-400">No options found</div>
              ) : (
                filteredOptions.map((option, index) => {
                  const value = typeof option === 'string' ? option : 
                              (option.account_id || option.id || option.key || option.value);
                  const displayText = getDisplayName(option);
                  const isSelected = selectedValues.includes(value);
                  
                  return (
                    <button
                      key={getOptionKey(option, index)}
                      type="button"
                      onClick={() => handleMultiToggle(field, value)}
                      className={`w-full px-3 py-2 text-sm text-left flex items-center justify-between ${
                        isSelected 
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
                          : 'hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-900 dark:text-slate-100'
                      }`}
                    >
                      <span>{displayText}</span>
                      {isSelected && (
                        <span className="text-blue-600 dark:text-blue-300">✓</span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm mb-4">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-base font-medium text-gray-900 dark:text-slate-100 flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Enhanced Filters & Controls
          </h2>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-wrap">
            <button
              onClick={() => setIsCollapsed((prev) => !prev)}
              className="px-3 py-1 text-xs border border-gray-300 dark:border-white/20 rounded flex items-center justify-center space-x-1 text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700"
              disabled={loading}
            >
              <span>{isCollapsed ? 'Show Filters' : 'Hide Filters'}</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : 'rotate-0'}`} />
            </button>
            
            <button
              onClick={onResetFilters}
              className="px-3 py-1 text-xs border border-gray-300 dark:border-white/20 rounded text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              Reset All
            </button>
            <button
              onClick={filtersChanged ? onApplyFilters : onRefreshData}
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors ${
                filtersChanged 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-green-600 hover:bg-green-700'
              } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  {filtersChanged ? 'Apply Changes' : 'Refresh Data'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {!isCollapsed && (
      <div className="p-4 sm:p-6">
        {/* Main Filter Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Project Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              📁 Project
            </label>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">The project the ticket is associated with</p>
            {renderMultiSelectDropdown(
              'project', 
              filterOptions.projects || [], 
              'Select project...', 
              <span>📁</span>
            )}
          </div>

          {/* Issue Assignee Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <span className="inline-flex items-center">
                <UserCheck className="w-4 h-4 inline mr-1 text-green-500" />
                Issue Assignee
                {Array.isArray(filters.project) && filters.project.length > 0 && filterOptions.loading && (
                  <RefreshCw className="w-3 h-3 text-gray-400 dark:text-slate-500 animate-spin ml-1" />
                )}
              </span>
            </label>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">Who the ticket is assigned to</p>
            {renderMultiSelectDropdown(
              'issueAssignee', 
              filterOptions.users || [], 
              'Select assignees...', 
              <UserCheck className="w-4 h-4 text-green-500" />
            )}
          </div>

          {/* Worklog Author Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <span className="inline-flex items-center">
                <User className="w-4 h-4 inline mr-1 text-blue-500" />
                Worklog Author
                {Array.isArray(filters.project) && filters.project.length > 0 && filterOptions.loading && (
                  <RefreshCw className="w-3 h-3 text-gray-400 dark:text-slate-500 animate-spin ml-1" />
                )}
              </span>
            </label>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">Who logged the hours</p>
            {renderMultiSelectDropdown(
              'worklogAuthor', 
              filterOptions.users || [], 
              'Select authors...', 
              <User className="w-4 h-4 text-blue-500" />
            )}
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              📊 Status
            </label>
            {renderMultiSelectDropdown(
              'status', 
              (filterOptions.statuses || []).map(status => ({ key: status, name: status })), 
              'Select statuses...', 
              <span>📊</span>
            )}
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <Tag className="w-4 h-4 inline mr-1 text-red-500" />
              Priority
            </label>
            {renderMultiSelectDropdown(
              'priority', 
              (filterOptions.priorities || []).map(priority => ({ key: priority, name: priority })), 
              'Select priorities...', 
              <Tag className="w-4 h-4 text-red-500" />
            )}
          </div>

          {/* Sprint Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <span className="inline-flex items-center">
                <GitBranch className="w-4 h-4 inline mr-1 text-purple-500" />
                Sprint
                {Array.isArray(filters.project) && filters.project.length > 0 && filterOptions.loading && (
                  <RefreshCw className="w-3 h-3 text-gray-400 dark:text-slate-500 animate-spin ml-1" />
                )}
              </span>
            </label>
            {renderMultiSelectDropdown(
              'sprint', 
              filterOptions.sprints || [], 
              'Select sprints...', 
              <GitBranch className="w-4 h-4 text-purple-500" />
            )}
          </div>
        </div>

        {/* Date Range and Hours Range Row */}
        <div className="flex flex-col sm:flex-row gap-4">

          {/* Enhanced Date Range with Predefined Periods */}
          <div className="w-full sm:w-80 sm:max-w-xs">
            <CalendarFilter
              filters={filters}
              onFilterChange={onFilterChange}
            />
          </div>
          {/* Hours Range */}
          <div className="w-full sm:w-auto min-w-0">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              ⏱️ Hours Range
            </label>
            <div className="flex space-x-2 min-w-0">
              <input
                type="number"
                placeholder="Min"
                value={filters.minHours || ''}
                onChange={(e) => onFilterChange('minHours', e.target.value)}
                className="flex-1 sm:w-24 min-w-0 px-3 py-2 border border-gray-300 dark:border-white/20 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-500"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.maxHours || ''}
                onChange={(e) => onFilterChange('maxHours', e.target.value)}
                className="flex-1 sm:w-24 min-w-0 px-3 py-2 border border-gray-300 dark:border-white/20 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-500"
              />
            </div>
          </div>          

        </div>
      </div>
      )}
    </div>
  );
};

export default TimeTrackingFilters;